import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import AsyncSelect from 'react-select/async';

import PropTypes from 'prop-types';

import API from '@/apiRequest';
import debounce from '@/helpers/debounce';
import { showToast } from '@/store/ducks/toasts';

const ClientsSelect = ({ handleSelectClient }) => {
  const [mappedClients, setMappedClients] = useState([]);
  const [clients, setClients] = useState([]);

  const [value, setValue] = useState('');

  const [currentPage, setCurrentPage] = useState(0);
  const [loadMore, setLoadMore] = useState(true);

  const [defaultMenuIsOpen, setDefaultMenuIsOpen] = useState(false);
  const [inputSearchValue, setInputSearchValue] = useState('');
  const [inputKey, setInputKey] = useState(1);

  const dispatch = useDispatch();
  const intl = useIntl();

  const handleSelectChange = selectValue => {
    setValue(selectValue);
    handleSelectClient(selectValue);
    setDefaultMenuIsOpen(false);
  };

  const handleInputChange = inputValue => {
    if (inputValue === inputSearchValue) return;

    setInputSearchValue(inputValue);
    handleFetchSuggestions(inputValue);
  };

  const handleMapClientsData = data => data?.map(client => ({ label: client.name, value: client.id })) || [];

  const handleFetchClients = async (search = '', page = 0) => {
    try {
      const { data } = await API.get('/commercial/clients', {
        params: {
          limit: 15,
          offset: page * 15,
          orderBy: 'created_at',
          orderByDir: 'desc',
          search: search || undefined,
        },
      });

      const fetchedClients = handleMapClientsData(data);

      if (fetchedClients.length < 15) setLoadMore(false);

      return fetchedClients;
    } catch (err) {
      console.debug('[DEBUG] ClientsSelect handleFetchClients error ', err);

      dispatch(
        showToast({
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'FETCH_EXTRACT_CLIENTS_DATA_ERROR' }),
          type: 'error',
          duration: 4000,
        })
      );
    }
  };

  const handleFetchSuggestions = async inputValue => {
    setDefaultMenuIsOpen(true);
    setCurrentPage(0);

    setClients(await handleFetchClients(inputValue));

    setInputKey(state => state + 1);
  };

  const handleLoadMore = async () => {
    if (loadMore) {
      setDefaultMenuIsOpen(true);
      setCurrentPage(state => state + 1);

      const moreClients = await handleFetchClients(inputSearchValue, currentPage + 1);

      setClients(state => [...state, ...moreClients]);
    }
  };

  useEffect(() => {
    const fetchInitialClients = async () => setClients(await handleFetchClients('', 0));

    fetchInitialClients();
  }, []);

  useEffect(() => {
    const mapped = clients.map(client => ({
      value: client.id,
      label: client.name,
      ...client,
    }));

    setMappedClients(mapped);
  }, [clients]);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '34px',
      borderColor: state.isFocused ? '#4d8fca' : '#e3e3e3',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#4d8fca',
      },
      fontSize: '12px',
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: '12px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#4d8fca' : state.isFocused ? '#f5f5f5' : '#ffffff',
      color: state.isSelected ? '#ffffff' : '#919191',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: state.isSelected ? '#4d8fca' : '#f5f5f5',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: provided => ({
      ...provided,
      color: '#b1b1b1',
      padding: '6px',
    }),
    multiValue: (base) => ({
      ...base,
      borderRadius: '15px',
      backgroundColor: '#658ec6',
      color: '#ffffff',
    }),
    multiValueLabel: (base) => ({
      ...base,
      padding: '2px 2px 2px 8px',
      fontSize: '12px',
      color: '#ffffff',
    }),
    multiValueRemove: (base) => ({
      ...base,
      borderRadius: '0 13px 13px 0',
      padding: '2px 5px 2px 2px',
      backgroundColor: '#658ec6',
      color: '#ffffff',
      '&:hover': {
        backgroundColor: '#4a6fa5', // darkened #658ec6
        color: '#ffffff',
      },
    }),
  };

  return (
    <AsyncSelect
      className="w-full"
      classNamePrefix="select"
      styles={selectStyles}
      name="clients"
      key={inputKey}
      defaultInputValue={inputSearchValue}
      onInputChange={debounce(handleInputChange, 500)}
      value={value}
      placeholder={intl.formatMessage({ id: 'CUSTOMERS' })}
      onMenuScrollToBottom={handleLoadMore}
      defaultOptions={mappedClients}
      noOptionsMessage={() => intl.formatMessage({ id: 'NO_SELECT_OPTIONS' })}
      onChange={handleSelectChange}
      defaultMenuIsOpen={defaultMenuIsOpen}
      escapeClearsValue
      isMulti
    />
  );
};

ClientsSelect.propTypes = {
  handleSelectClient: PropTypes.func.isRequired,
};

export default ClientsSelect;
